import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api";
import toastr from "toastr";

/* ─── Types ──────────────────────────────────────────────────────────────────── */
interface TestCategory {
  id: number;
  category_name: string;
  type: "single" | "group";
  amount?: number;
}

interface LabTest {
  id: number;
  test_name: string;
  price: number;
  test_category: number;
}

interface SelectedTest {
  item_id: string | number;
  item_type: "test" | "single" | "group";
  item_name: string;
  item_category: number;
  item_rate: number;
  item_quantity: number;
  item_uom: string;
  item_amount: number;
  item_gst: number;
}

interface Doctor {
  id: number;
  name?: string;
  d_name?: string;
}

interface PatientInfo {
  id: number;
  P_name: string;
  prefix: string;
  P_code: string;
}

const getDoctorName = (d: Doctor): string => d.name || d.d_name || "";

const PatientFilter: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  /* form state */
  const [billDate, setBillDate]           = useState(new Date().toISOString().split("T")[0]);
  const [entryTime, setEntryTime]         = useState(new Date().toTimeString().slice(0, 5));
  const [billNo, setBillNo]               = useState("");
  const [refPrefix, setRefPrefix]         = useState("Self.");
  const [reference, setReference]         = useState("Self");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [referenceNo, setReferenceNo]     = useState("");
  const [homeVisit, setHomeVisit]         = useState(0);
  const [discount, setDiscount]           = useState(0); // now a PERCENTAGE value (0-100)
  const [payAmount, setPayAmount]         = useState<number | "">("");
  const [createdBy, setCreatedBy]         = useState("");

  /* data */
  const [patientInfo, setPatientInfo]           = useState<PatientInfo | null>(null);
  const [singleCategories, setSingleCategories] = useState<TestCategory[]>([]);
  const [groupCategories, setGroupCategories]   = useState<TestCategory[]>([]);
  const [selectedDeptCat, setSelectedDeptCat]   = useState<number | null>(null);
  const [deptTests, setDeptTests]               = useState<LabTest[]>([]);
  const [searchResults, setSearchResults]       = useState<LabTest[]>([]);
  const [testSearch, setTestSearch]             = useState("");
  const [doctors, setDoctors]                   = useState<Doctor[]>([]);
  const [billItems, setBillItems]               = useState<SelectedTest[]>([]);
  const [selectedTestIds, setSelectedTestIds]   = useState<string[]>([]);

  const [modalTab, setModalTab]                 = useState<"department" | "profile">("department");
  const [showModal, setShowModal]               = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successBillId, setSuccessBillId]       = useState<number | null>(null);
  const [successBillNo, setSuccessBillNo]       = useState("");

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Cache of all tests seen so far (across category switches) keyed by their id string
  const seenTestsRef = useRef<Map<string, LabTest>>(new Map());

  /* computed totals */
  const totalAmount   = billItems.reduce((s, i) => s + Number(i.item_amount || 0), 0);
  // Discount is a percentage of the total amount. Compute the actual ₹ discount from it.
  const discountAmount = (totalAmount * (Number(discount) || 0)) / 100;
  const netAmount     = totalAmount + (homeVisit || 0) - discountAmount;
  const balanceAmount = payAmount !== "" ? Number(payAmount) - netAmount : 0;

  useEffect(() => { if (id) fetchInitial(); }, [id]);
  useEffect(() => { if (selectedDeptCat) fetchDeptTests(selectedDeptCat); }, [selectedDeptCat]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!testSearch.trim()) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(() => doSearch(testSearch), 300);
  }, [testSearch]);

  const fetchInitial = async () => {
    try {
      const [patRes, catRes, billNoRes, docRes] = await Promise.all([
        API.get(`/patients/${id}`),
        API.get("/patients/categories"),
        API.get("/patients/generateBillNo"),
        API.get("/patients/doctors"),
      ]);
      setPatientInfo(patRes.data?.data || null);
      const cats: TestCategory[] = Array.isArray(catRes.data?.data) ? catRes.data.data : [];
      setSingleCategories(cats.filter((c) => c.type === "single"));
      setGroupCategories(cats.filter((c) => c.type === "group"));
      setBillNo(billNoRes.data?.bill_no || "");
      const docList: Doctor[] = Array.isArray(docRes.data?.data) ? docRes.data.data : [];
      setDoctors(docList);
      const firstSingle = cats.find((c) => c.type === "single");
      if (firstSingle) setSelectedDeptCat(firstSingle.id);
    } catch (err) {
      console.error("fetchInitial error:", err);
      toastr.error("Failed to load data");
    }
  };

  const fetchDeptTests = async (catId: number) => {
    try {
      const res = await API.get(`/patients/tests/${catId}`);
      const tests: LabTest[] = Array.isArray(res.data?.data) ? res.data.data : [];
      // Cache every test we ever load so selections survive category switches
      tests.forEach((t) => seenTestsRef.current.set(String(t.id), t));
      setDeptTests(tests);
    } catch (err) {
      console.error("fetchDeptTests error:", err);
      toastr.error("Failed to load tests");
    }
  };

  const doSearch = async (q: string) => {
    try {
      const res = await API.get(`/patients/testsearch?search=${encodeURIComponent(q)}`);
      const results: LabTest[] = Array.isArray(res.data?.data) ? res.data.data : [];
      // Cache search results too
      results.forEach((t) => seenTestsRef.current.set(String(t.id), t));
      setSearchResults(results);
    } catch { /* ignore */ }
  };

  const toggleTest = (key: string) => {
    setSelectedTestIds((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const isChecked = (key: string) => selectedTestIds.includes(key);

  const buildRow = (
    itemId: string | number,
    itemType: "test" | "single" | "group",
    testName: string,
    testPrice: number,
    testCategory: number
  ): SelectedTest => {
    const price = Number(testPrice) || 0;
    return {
      item_id: itemId,
      item_type: itemType,
      item_name: testName || "",
      item_category: testCategory,
      item_rate: price,
      item_quantity: 1,
      item_uom: "LF",
      item_amount: price,
      item_gst: 0,
    };
  };

  const handleAddTests = () => {
    if (selectedTestIds.length === 0) {
      alert("Please select at least one test");
      return;
    }
    try {
      const existingIds = new Set(billItems.map((b) => String(b.item_id)));
      const newRows: SelectedTest[] = [];

      selectedTestIds.forEach((key) => {
        if (existingIds.has(key)) return; // already in bill

        // Check group categories
        if (key.startsWith("group_")) {
          const g = groupCategories.find((x) => `group_${x.id}` === key);
          if (g) newRows.push(buildRow(key, "group", g.category_name, g.amount || 0, g.id));
          return;
        }
        // Check single categories
        if (key.startsWith("single_")) {
          const s = singleCategories.find((x) => `single_${x.id}` === key);
          if (s) newRows.push(buildRow(key, "single", s.category_name, s.amount || 0, s.id));
          return;
        }
        // Regular test — look in current dept, search results, then cache
        const t =
          deptTests.find((x) => String(x.id) === key) ||
          searchResults.find((x) => String(x.id) === key) ||
          seenTestsRef.current.get(key);
        if (t) newRows.push(buildRow(t.id, "test", t.test_name, t.price, t.test_category));
      });

      if (newRows.length === 0) {
        toastr.warning("Selected tests are already added");
      } else {
        setBillItems((prev) => [...prev, ...newRows]);
        toastr.success(`${newRows.length} test(s) added`);
      }
      // Clear selections so reopening the modal starts fresh
      setSelectedTestIds([]);
      setShowModal(false);
    } catch (err) {
      console.error("handleAddTests error:", err);
      toastr.error("Failed to add tests. Please try again.");
    }
  };

  const handleRemoveRow = (idx: number) => {
    const removed = billItems[idx];
    if (!removed) return;
    setSelectedTestIds((prev) => prev.filter((k) => k !== String(removed.item_id)));
    setBillItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (billItems.length === 0) { alert("Please select at least one test"); return; }
    setSaving(true);
    try {
      const res = await API.post("/patients/saveEntry", {
        bill_no: billNo,
        bill_date: billDate,
        entry_time: entryTime,
        patient_id: id,
        ref_prefix: refPrefix,
        reference,
        total_amount: totalAmount,
        net_amount: netAmount,
        home_visit: homeVisit,
        discount,                 // percentage entered by user
        discount_amount: discountAmount, // computed ₹ amount actually deducted
        pay_amount: payAmount || 0,
        payment_method: paymentMethod,
        reference_no: referenceNo,
        created_by: createdBy,
        sales: billItems,
      });
      if (res.data?.success) {
        setSuccessBillId(res.data.bill_id);
        setSuccessBillNo(res.data.bill_no || billNo);
        setShowSuccessModal(true);
        setBillItems([]);
        setSelectedTestIds([]);
        setPayAmount("");
        setDiscount(0);
        setHomeVisit(0);
        const nb = await API.get("/patients/generateBillNo");
        setBillNo(nb.data?.bill_no || "");
      }
    } catch (err) {
      console.error("handleSubmit error:", err);
      toastr.error("Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  const resolveTestName = (key: string): string => {
    // Look in current dept/search, then in the global cache
    const t =
      deptTests.find((x) => String(x.id) === key) ||
      searchResults.find((x) => String(x.id) === key) ||
      seenTestsRef.current.get(key);
    if (t) return t.test_name || key;
    const g = groupCategories.find((x) => `group_${x.id}` === key);
    if (g) return g.category_name || key;
    const s = singleCategories.find((x) => `single_${x.id}` === key);
    if (s) return s.category_name || key;
    return key;
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">Patient Entry</h4>
          {patientInfo && (
            <small className="text-muted">
              {patientInfo.prefix}. {patientInfo.P_name}&nbsp;|&nbsp;{patientInfo.P_code}
            </small>
          )}
        </div>
        <button className="btn btn-light" onClick={() => navigate("/patients")}>
          <i className="fa fa-arrow-left me-1" /> Back to List
        </button>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">

              {/* Row 1 */}
              <div className="row mb-3">
                <div className="col-md-2">
                  <label className="form-label">Entry Date</label>
                  <input type="date" className="form-control" value={billDate}
                    onChange={(e) => setBillDate(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Entry Time</label>
                  <input type="time" className="form-control" value={entryTime}
                    onChange={(e) => setEntryTime(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Bill No.</label>
                  <input type="text" className="form-control" value={billNo} readOnly />
                </div>
                <div className="col-md-5">
                  <label className="form-label">Referred By</label>
                  <div className="row">
                    <div className="col-5">
                      <select className="form-control" value={refPrefix}
                        onChange={(e) => {
                          setRefPrefix(e.target.value);
                          setReference(e.target.value === "Self." ? "Self" : "");
                        }}>
                        <option value="Self.">Self.</option>
                        <option value="Dr.">Dr.</option>
                      </select>
                    </div>
                    <div className="col-7">
                      {refPrefix === "Dr." ? (
                        <select className="form-control" value={reference}
                          onChange={(e) => setReference(e.target.value)}>
                          <option value="">-- Select Doctor --</option>
                          {doctors.map((d) => (
                            <option key={d.id} value={getDoctorName(d)}>
                              {getDoctorName(d)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input type="text" className="form-control" value="Self" readOnly />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Select Test Button */}
              <div className="row mb-3">
                <div className="col-md-2">
                  <button className="btn btn-light" onClick={() => { setSelectedTestIds([]); setShowModal(true); }}>
                    Select Test
                  </button>
                </div>
              </div>

              {/* Bill Items Table */}
              <div className="table-responsive mb-3">
                <table className="table table-bordered">
                  <thead>
                    <tr style={{ backgroundColor: "#81888c", color: "white" }}>
                      <td className="text-center" style={{ width: 50 }}>S.No</td>
                      <td>Description</td>
                      <td className="text-end">Amount</td>
                      <td className="text-center" style={{ width: 70 }}>Remove</td>
                    </tr>
                  </thead>
                  <tbody>
                    {billItems.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-3">
                          No tests selected. Click "Select Test" to add tests.
                        </td>
                      </tr>
                    ) : (
                      billItems.map((item, idx) => (
                        <tr key={`${item.item_id}-${idx}`}>
                          <td className="text-center">{idx + 1}</td>
                          <td>{item.item_name}</td>
                          <td className="text-end">₹{Number(item.item_amount || 0).toFixed(2)}</td>
                          <td className="text-center">
                            <button className="btn btn-light"
                              onClick={() => handleRemoveRow(idx)}>
                              <i className="fa fa-trash" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Billing Summary */}
              <div className="row">
                <div className="col-md-6">
                  <div className="row mb-2">
                    <label className="col-sm-4 col-form-label">Payment Method</label>
                    <div className="col-sm-8">
                      <select className="form-control" value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}>
                        <option>Cash</option>
                        <option>Credit Card</option>
                        <option>Debit Card</option>
                        <option>Online Payment</option>
                        <option>Cheque</option>
                        <option>Demand Draft</option>
                      </select>
                    </div>
                  </div>
                  <div className="row mb-2">
                    <label className="col-sm-4 col-form-label">Reference No.</label>
                    <div className="col-sm-8">
                      <input type="text" className="form-control" maxLength={100}
                        value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <label className="col-sm-4 col-form-label">Created By</label>
                    <div className="col-sm-8">
                      <input type="text" className="form-control" maxLength={100}
                        value={createdBy} onChange={(e) => setCreatedBy(e.target.value)} />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="row mb-2">
                    <label className="col-sm-4 col-form-label">Discount (%)</label>
                    <div className="col-sm-8">
                      <input
                        type="number"
                        className="form-control text-end"
                        min={0}
                        max={100}
                        step="0.01"
                        value={discount}
                        onChange={(e) => {
                          let val = Number(e.target.value) || 0;
                          if (val < 0) val = 0;
                          if (val > 100) val = 100;
                          setDiscount(val);
                        }}
                      />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <label className="col-sm-4 col-form-label">Discount Amount</label>
                    <div className="col-sm-8">
                      <input
                        type="text"
                        className="form-control text-end"
                        value={`₹${Number(discountAmount).toFixed(2)}`}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <label className="col-sm-4 col-form-label">Home Visit</label>
                    <div className="col-sm-8">
                      <input type="number" className="form-control text-end" value={homeVisit}
                        onChange={(e) => setHomeVisit(Number(e.target.value) || 0)} />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <label className="col-sm-4 col-form-label">Total</label>
                    <div className="col-sm-8">
                      <input type="text" className="form-control text-end"
                        value={`₹${Number(totalAmount).toFixed(2)}`} readOnly />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <label className="col-sm-4 col-form-label fw-bold">Net Total</label>
                    <div className="col-sm-8">
                      <input type="text" className="form-control text-end fw-bold"
                        value={`₹${Number(netAmount).toFixed(2)}`} readOnly />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <label className="col-sm-4 col-form-label">Pay Amount</label>
                    <div className="col-sm-8">
                      <input type="number" className="form-control text-end" value={payAmount}
                        onChange={(e) =>
                          setPayAmount(e.target.value === "" ? "" : Number(e.target.value))
                        } />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <label className="col-sm-4 col-form-label">Balance Amount</label>
                    <div className="col-sm-8">
                      <input type="text" className="form-control text-end"
                        value={payAmount !== "" ? `₹${Number(balanceAmount).toFixed(2)}` : ""}
                        readOnly
                        style={{
                          color: balanceAmount < 0 ? "red" : balanceAmount > 0 ? "green" : "inherit"
                        }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button className="btn btn-light" onClick={() => navigate("/patients")}>
                  <i className="fa fa-arrow-left me-1" /> Back to List
                </button>
                <button className="btn btn-light" onClick={handleSubmit} disabled={saving}>
                  {saving
                    ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</>
                    : <><i className="fa fa-check me-1" />Save</>
                  }
                </button>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Test Selection Modal */}
      {showModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedTestIds([]);
              setShowModal(false);
            }
          }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Select Tests — {patientInfo ? `${patientInfo.prefix}. ${patientInfo.P_name}` : ""}
                </h5>
                <button type="button" className="btn-close" onClick={() => { setSelectedTestIds([]); setShowModal(false); }} />
              </div>
              <div className="modal-body">
                <ul className="nav nav-pills mb-3 align-items-center gap-2">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${modalTab === "department" ? "active" : ""}`}
                      onClick={() => { setModalTab("department"); setTestSearch(""); setSearchResults([]); }}
                    >
                      Department
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${modalTab === "profile" ? "active" : ""}`}
                      onClick={() => { setModalTab("profile"); setTestSearch(""); setSearchResults([]); }}
                    >
                      Profile
                    </button>
                  </li>
                  {modalTab === "department" && (
                    <li className="ms-auto">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search test..."
                        value={testSearch}
                        onChange={(e) => setTestSearch(e.target.value)}
                      />
                    </li>
                  )}
                </ul>

                {modalTab === "department" && (
                  <div className="row">
                    {!testSearch && (
                      <div className="col-md-4">
                        <select
                          className="form-control"
                          size={Math.max(singleCategories.length, 6)}
                          value={selectedDeptCat ?? ""}
                          onChange={(e) => {
                            setSelectedDeptCat(Number(e.target.value));
                          }}
                        >
                          {singleCategories.map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className={testSearch ? "col-md-12" : "col-md-8"}>
                      <div style={{ height: 220, overflowY: "auto" }}>
                        {(testSearch ? searchResults : deptTests).map((test) => {
                          const key = String(test.id);
                          const checked = isChecked(key);
                          return (
                            <div
                              key={test.id}
                              className="d-flex align-items-center justify-content-between px-2 py-1 mb-1 rounded"
                              style={{
                                backgroundColor: checked ? "#DC143C" : "#808000",
                                color: "white",
                                cursor: "pointer",
                                userSelect: "none",
                              }}
                              onClick={() => toggleTest(key)}
                            >
                              <span>{test.test_name}</span>
                              <div className="d-flex align-items-center gap-2">
                                <span>₹{test.price}</span>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  readOnly
                                />
                              </div>
                            </div>
                          );
                        })}
                        {(testSearch ? searchResults : deptTests).length === 0 && (
                          <p className="text-muted text-center mt-3">
                            {testSearch ? "No results found" : "No tests in this category"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {modalTab === "profile" && (
                  <div style={{ height: 220, overflowY: "auto" }}>
                    {groupCategories.map((g) => {
                      const key = `group_${g.id}`;
                      const checked = isChecked(key);
                      return (
                        <div
                          key={g.id}
                          className="d-flex align-items-center justify-content-between px-2 py-1 mb-1 rounded"
                          style={{
                            backgroundColor: checked ? "#DC143C" : "#808000",
                            color: "white",
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                          onClick={() => toggleTest(key)}
                        >
                          <span>{g.category_name}</span>
                          <div className="d-flex align-items-center gap-2">
                            <span>₹{g.amount || 0}</span>
                            <input
                              type="checkbox"
                              checked={checked}
                              readOnly
                            />
                          </div>
                        </div>
                      );
                    })}
                    {groupCategories.length === 0 && (
                      <p className="text-muted text-center mt-3">No profiles available</p>
                    )}
                  </div>
                )}

                {selectedTestIds.length > 0 && (
                  <div className="mt-3 p-2 border rounded bg-light">
                    <small className="text-muted d-block mb-1">
                      Selected ({selectedTestIds.length}):
                    </small>
                    <div className="d-flex flex-wrap gap-1">
                      {selectedTestIds.map((key) => (
                        <span key={key} className="badge bg-primary d-flex align-items-center gap-1">
                          {resolveTestName(key)}
                          <button
                            type="button"
                            className="btn-close btn-close-white"
                            style={{ fontSize: "0.6rem" }}
                            onClick={() => toggleTest(key)}
                          />
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleAddTests}>Add</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmation Alert</h5>
                <button type="button" className="btn-close"
                  onClick={() => setShowSuccessModal(false)} />
              </div>
              <div className="modal-body">
                <p>Your New Entry <code>{successBillNo}</code> Created Successfully!</p>
                <div className="d-flex gap-2 mt-3">
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setShowSuccessModal(false);
                      if (successBillId) navigate(`/lab/BillPdf/${successBillId}`);
                    }}
                  >
                    <i className="fa fa-file-pdf-o me-1" /> View Receipt (A4)
                  </button>
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setShowSuccessModal(false);
                      if (successBillId) navigate(`/lab/posreciept/${successBillId}`);
                    }}
                  >
                    <i className="fa fa-print me-1" /> View Receipt (POS)
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-light"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(`/patientlog/${id}`);
                  }}
                >
                  View Patient Log
                </button>
                <button className="btn btn-light" onClick={() => setShowSuccessModal(false)}>
                  Stay Here
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientFilter;